import { useEffect, useRef } from 'react';
import { connectStoreQueue, isForbiddenSocketError } from '../api/storeSocket';
import { useAuth } from '../context/AuthContext';
import { useOrders } from '../context/OrdersContext';

const REFETCH_DEBOUNCE_MS = 300;

function isStaff(groups) {
  return !!groups?.some((group) => group === 'Ops' || group === 'Admin');
}

/**
 * While the order queue is mounted, subscribe to store:queue and debounce a
 * REST refetch. Confirm / pack / assign stay on HTTP.
 */
export default function useStoreQueueLive(onChange) {
  const { user } = useAuth();
  const { notify } = useOrders();
  const onChangeRef = useRef(onChange);
  const notifyRef = useRef(notify);
  onChangeRef.current = onChange;
  notifyRef.current = notify;

  const accessToken = user?.accessToken;
  const staff = isStaff(user?.groups);

  useEffect(() => {
    const endpoint = process.env.REACT_APP_WS_URL;
    if (!endpoint || !accessToken || !staff) return undefined;

    let debounceTimer = null;
    let toldForbidden = false;
    const connection = connectStoreQueue({
      endpoint,
      accessToken,
      onEvent: () => {
        if (debounceTimer != null) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          debounceTimer = null;
          onChangeRef.current?.();
        }, REFETCH_DEBOUNCE_MS);
      },
      onSocketError: (message) => {
        if (!isForbiddenSocketError(message) || toldForbidden) return;
        toldForbidden = true;
        notifyRef.current('Live queue updates are not available for this account.');
      },
    });

    return () => {
      if (debounceTimer != null) clearTimeout(debounceTimer);
      connection.close();
    };
  }, [accessToken, staff]);
}
